import { isSameDay } from 'date-fns';
import React, { useContext, useEffect, useRef } from 'react';
import { useField } from 'react-final-form';

import Input from '@swf/components/Inputs/Input';
import StarsDatePicker from '@swf/components/Inputs/StarsDatePicker';
import Text from '@swf/components/Inputs/Text';
import Translation from '@swf/components/Translation/Translation';
import type { RecurringTournament } from '@swf/generated/types/api/v0/SwHomeGames';
import { useGetCurrentDate } from '@swf/poker';

import Container from '../../Container/Container';
import CreateTournamentContext from '../../CreateTournamentContext/CreateTournamentContext';
import RecurrenceToggle from '../../Recurrence/Toggle/Toggle';
import Time from '../../assets/time.svg';
import useSaveFormFields from '../../hooks/useSaveFormFields';
import { ModalPages } from '../../utils/common';

import {
  TXTCLI_Notes,
  TXTCLI_Start_Date,
  TXTCLI_Start_Time,
  TXTCLI_Tournament_Name
} from './MainInfoForm.messages.json';
import styles from './MainInfoForm.scss';

const formatDate = (date: Date) => `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;

const MainInfoForm: React.FC = () => {
  const { setState } = useContext(CreateTournamentContext);
  const timePickerRef = useRef<HTMLDivElement>(null);
  const handleTimeIconClick = () => {
    const input = timePickerRef?.current?.querySelector('input:not([type=hidden])') as HTMLElement;

    input && input.click();
  };
  const date = new Date();
  const forwardDate = new Date(date.setMinutes(date.getMinutes() + 10));
  const { now, timeZone } = useGetCurrentDate(forwardDate);
  const { locale } = Intl.DateTimeFormat().resolvedOptions();
  const isLocale12h = Intl.DateTimeFormat(locale, { hour: 'numeric' }).resolvedOptions()?.hour12;
  const altTimeFormat = isLocale12h ? 'h:i K' : 'H:i';
  const {
    input: { value }
  } = useField('startDate', { defaultValue: formatDate(now) });
  useField('gameName', { defaultValue: '' });
  useField('startTime', { defaultValue: now });
  useField('repeatEvery', { defaultValue: 1 });
  useField('recurringPatternType', {
    defaultValue: 'HG_RECURRING_DAILY' as RecurringTournament['recurringPatternType']
  });
  useSaveFormFields();

  useEffect(
    () => setState((previousState) => ({ ...previousState, activePage: ModalPages.MainInfo })),
    [setState]
  );

  return (
    <Container>
      <div className={styles.mainInfo}>
        <Text
          id="gameName"
          label={TXTCLI_Tournament_Name}
          className={styles.input}
          showValidationTick={false}
        />
        <div className={styles.datePicker}>
          <StarsDatePicker
            id="startDate"
            label={<Translation {...TXTCLI_Start_Date} />}
            className={styles.picker}
            altFormat="j F, l"
            hideDateFormat
            minDate={formatDate(now)}
            defaultDate={formatDate(now)}
          />
        </div>
        <div className={styles.timePicker} ref={timePickerRef}>
          <StarsDatePicker
            id="startTime"
            label={<Translation {...TXTCLI_Start_Time} />}
            className={styles.picker}
            mode="time"
            enableTime
            noCalendar
            time_24hr={!isLocale12h}
            defaultHour={now.getHours()}
            defaultMinute={now.getMinutes()}
            altFormat={altTimeFormat}
            icon={<Time onClick={handleTimeIconClick} />}
            hideDateFormat
            minTime={
              isSameDay(now, new Date(value)) ? `${now.getHours()}:${now.getMinutes()}` : undefined
            }
          />
          <div className={styles.timezone}>{timeZone}</div>
        </div>
        <RecurrenceToggle />
        <Input
          id="notes"
          label={TXTCLI_Notes}
          className={styles.input}
          showValidationTick={false}
          type="textarea"
        />
      </div>
    </Container>
  );
};

export default MainInfoForm;
